// src/components/MallOperationsTab.js
// Mall Admin → Operations tab
// Shows POS push failure ratio per vendor + stuck orders drill-down
import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';

const POS_LABELS = {
  FOODICS: 'Foodics', SQUARE: 'Square', TOAST: 'Toast',
  LIGHTSPEED: 'Lightspeed', REVEL: 'Revel', CLOVER: 'Clover',
  PETPOOJA: 'PetPooja', GENERIC_WEBHOOK: 'Webhook',
};

function StatusDot({ ok }) {
  return (
    <span style={{ display:'inline-block', width:8, height:8, borderRadius:'50%',
      background: ok ? 'var(--green)' : 'var(--red)', marginRight:6, flexShrink:0 }} />
  );
}

function FailBadge({ rate }) {
  const color = rate === 0 ? 'var(--green)' : rate < 20 ? '#f59e0b' : 'var(--red)';
  return (
    <span style={{ background: color + '22', color, padding:'2px 8px', borderRadius:4,
      fontWeight:700, fontSize:12 }}>
      {rate}%
    </span>
  );
}

function StatCard({ label, value, sub, accent }) {
  return (
    <div style={{ background:'var(--card)', border:'1px solid var(--border)', borderRadius:10,
      padding:'14px 18px', minWidth:120 }}>
      <div style={{ fontSize:11, color:'var(--muted)', textTransform:'uppercase',
        letterSpacing:'0.06em', marginBottom:6 }}>{label}</div>
      <div style={{ fontSize:24, fontWeight:700, color: accent || 'var(--fg)' }}>{value}</div>
      {sub && <div style={{ fontSize:11, color:'var(--muted)', marginTop:4 }}>{sub}</div>}
    </div>
  );
}

export default function MallOperationsTab({ mallId }) {
  const [data, setData]         = useState(null);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState(null);
  const [hours, setHours]       = useState(24);
  const [expanded, setExpanded] = useState({});

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const r = await api.get(`/mall/${mallId}/operations-health?hours=${hours}`);
      setData(r.data);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally { setLoading(false); }
  }, [mallId, hours]);

  useEffect(() => { load(); }, [load]);

  const toggle = id => setExpanded(x => ({ ...x, [id]: !x[id] }));

  const s = {
    section: { marginBottom:24 },
    table: { width:'100%', borderCollapse:'collapse', fontSize:13 },
    th: { padding:'8px 12px', textAlign:'left', color:'var(--muted)', fontWeight:600,
      fontSize:11, textTransform:'uppercase', letterSpacing:'0.05em',
      borderBottom:'1px solid var(--border)', background:'var(--card)' },
    td: { padding:'10px 12px', borderBottom:'1px solid var(--border)', verticalAlign:'top' },
    row: { cursor:'pointer', transition:'background 0.15s' },
  };

  return (
    <div style={{ color:'var(--fg)', fontFamily:'inherit' }}>

      {/* ── Header ── */}
      <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:20, flexWrap:'wrap' }}>
        <h2 style={{ margin:0, fontSize:18, fontWeight:700 }}>Operations Health</h2>
        <div style={{ display:'flex', gap:8, marginLeft:'auto', alignItems:'center' }}>
          <select value={hours} onChange={e => setHours(Number(e.target.value))}
            style={{ background:'var(--card)', color:'var(--fg)', border:'1px solid var(--border)',
              borderRadius:6, padding:'6px 10px', fontSize:13 }}>
            <option value={6}>Last 6h</option>
            <option value={24}>Last 24h</option>
            <option value={48}>Last 48h</option>
            <option value={168}>Last 7d</option>
          </select>
          <button onClick={load} disabled={loading}
            style={{ background:'var(--accent)', color:'#fff', border:'none', borderRadius:6,
              padding:'7px 16px', cursor:'pointer', fontWeight:600, fontSize:13 }}>
            {loading ? '…' : '↺ Refresh'}
          </button>
        </div>
      </div>

      {error && (
        <div style={{ background:'#ef444422', border:'1px solid #ef4444', borderRadius:8,
          padding:'12px 16px', color:'#ef4444', marginBottom:16 }}>
          ⚠️ {error}
        </div>
      )}

      {!data && !loading && (
        <div style={{ color:'var(--muted)', padding:24 }}>No data yet — click Refresh.</div>
      )}

      {data && (
        <>
          {/* ── Summary cards ── */}
          <div style={{ display:'flex', gap:12, flexWrap:'wrap', marginBottom:24 }}>
            <StatCard label="POS Push Total"  value={data.totals.totalPushed} sub={`Last ${hours}h`} />
            <StatCard label="Push Failed"     value={data.totals.totalFailed}
              accent={data.totals.totalFailed > 0 ? 'var(--red)' : 'var(--green)'} />
            <StatCard label="Fail Rate"       value={`${data.totals.overallFailRate}%`}
              accent={data.totals.overallFailRate > 10 ? 'var(--red)' : 'var(--green)'} />
            <StatCard label="Stuck Orders"    value={data.totals.totalStuck}
              accent={data.totals.totalStuck > 0 ? '#f59e0b' : 'var(--green)'}
              sub="> 15 min old" />
            <StatCard label="Vendors"         value={data.vendors.length} />
          </div>

          {/* ── Per-vendor table ── */}
          <div style={s.section}>
            <h3 style={{ margin:'0 0 12px', fontSize:14, fontWeight:700, color:'var(--muted)',
              textTransform:'uppercase', letterSpacing:'0.05em' }}>
              Vendor POS Push Detail
            </h3>
            <div style={{ overflowX:'auto', borderRadius:10, border:'1px solid var(--border)' }}>
              <table style={s.table}>
                <thead>
                  <tr>
                    <th style={s.th}>Vendor</th>
                    <th style={s.th}>POS</th>
                    <th style={s.th}>Push On</th>
                    <th style={s.th}>Total</th>
                    <th style={s.th}>Success</th>
                    <th style={s.th}>Failed</th>
                    <th style={s.th}>Pending</th>
                    <th style={s.th}>Fail %</th>
                    <th style={s.th}>Last Failure</th>
                    <th style={s.th}>Stuck</th>
                  </tr>
                </thead>
                <tbody>
                  {data.vendors.map(v => (
                    <React.Fragment key={v.vendorId}>
                      <tr style={s.row} onClick={() => v.stuckOrders > 0 && toggle(v.vendorId)}
                        onMouseEnter={e => e.currentTarget.style.background='var(--hover)'}
                        onMouseLeave={e => e.currentTarget.style.background=''}>
                        <td style={s.td}>
                          <div style={{ display:'flex', alignItems:'center' }}>
                            <StatusDot ok={v.isActive} />
                            <span style={{ fontWeight:600 }}>{v.vendorName}</span>
                          </div>
                          <div style={{ fontSize:11, color:'var(--muted)', marginTop:2 }}>{v.status}</div>
                        </td>
                        <td style={s.td}>
                          {v.pos ? (
                            <span style={{ fontSize:12 }}>{POS_LABELS[v.pos.posType] || v.pos.posType}</span>
                          ) : (
                            <span style={{ color:'var(--muted)', fontSize:12 }}>—</span>
                          )}
                        </td>
                        <td style={s.td}>
                          {v.pos ? (
                            <span style={{ color: v.pos.pushEnabled ? 'var(--green)' : 'var(--muted)', fontSize:12 }}>
                              {v.pos.pushEnabled ? '✅' : '⏸'}
                            </span>
                          ) : '—'}
                        </td>
                        <td style={s.td}>{v.posPush.total}</td>
                        <td style={{ ...s.td, color:'var(--green)' }}>{v.posPush.success}</td>
                        <td style={{ ...s.td, color: v.posPush.failed > 0 ? 'var(--red)' : 'inherit' }}>
                          {v.posPush.failed}
                        </td>
                        <td style={{ ...s.td, color: v.posPush.pending > 0 ? '#f59e0b' : 'inherit' }}>
                          {v.posPush.pending}
                        </td>
                        <td style={s.td}><FailBadge rate={v.posPush.failRate} /></td>
                        <td style={s.td}>
                          {v.posPush.lastFailureAt ? (
                            <span style={{ fontSize:11, color:'var(--red)' }}>
                              {new Date(v.posPush.lastFailureAt).toLocaleTimeString()}
                            </span>
                          ) : <span style={{ color:'var(--green)', fontSize:12 }}>None</span>}
                        </td>
                        <td style={s.td}>
                          {v.stuckOrders > 0 ? (
                            <span style={{ color:'#f59e0b', fontWeight:700, cursor:'pointer' }}>
                              ⚠️ {v.stuckOrders} {expanded[v.vendorId] ? '▲' : '▼'}
                            </span>
                          ) : (
                            <span style={{ color:'var(--green)', fontSize:12 }}>✓</span>
                          )}
                        </td>
                      </tr>

                      {/* ── Stuck order drill-down ── */}
                      {expanded[v.vendorId] && v.stuckOrderDetails.length > 0 && (
                        <tr>
                          <td colSpan={10} style={{ padding:0 }}>
                            <div style={{ background:'#f59e0b11', borderLeft:'3px solid #f59e0b',
                              padding:'12px 16px', fontSize:12 }}>
                              <div style={{ fontWeight:700, marginBottom:8, color:'#f59e0b' }}>
                                Stuck Orders — {v.vendorName}
                              </div>
                              <table style={{ ...s.table, fontSize:12 }}>
                                <thead>
                                  <tr>
                                    <th style={{ ...s.th, fontSize:11 }}>Order ID</th>
                                    <th style={{ ...s.th, fontSize:11 }}>Status</th>
                                    <th style={{ ...s.th, fontSize:11 }}>Age</th>
                                    <th style={{ ...s.th, fontSize:11 }}>Total</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {v.stuckOrderDetails.map(o => (
                                    <tr key={o.id}>
                                      <td style={s.td}><code style={{ fontSize:11 }}>{o.id.slice(0,8)}…</code></td>
                                      <td style={s.td}>{o.status}</td>
                                      <td style={{ ...s.td, color:'#f59e0b', fontWeight:600 }}>
                                        {o.ageMinutes}m
                                      </td>
                                      <td style={s.td}>AED {o.total?.toFixed(2) || '—'}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
              {data.vendors.length === 0 && (
                <div style={{ padding:24, color:'var(--muted)', textAlign:'center' }}>
                  No active vendors found.
                </div>
              )}
            </div>
          </div>

          <div style={{ fontSize:11, color:'var(--muted)', marginTop:8 }}>
            Last refreshed: {new Date().toLocaleTimeString()} · Window: {hours}h ·
            Stuck = PENDING/CONFIRMED orders older than 15 min
          </div>
        </>
      )}
    </div>
  );
}
